import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wallet,
  Coins,
  Gift,
  Users,
  Sparkles,
  Trophy,
  TrendingUp,
  CheckCircle2,
  Clock,
  XCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Share2,
  LogOut,
  ExternalLink,
  Award,
  Target,
  ArrowRight,
  Store,
  UserPlus,
  Megaphone,
  Sparkle,
} from 'lucide-react';
import { useNavigate as _nav } from 'react-router-dom';
import AmbassadorShareCard from '../components/AmbassadorShareCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ----- Reusable atoms ----------------------------------------------------- */

const StatCard = ({ icon: Icon, label, value, sub, gold = false, testid }) => (
  <div
    className={`rounded-3xl border p-5 sm:p-6 ${
      gold
        ? 'border-gold-500/40 bg-gradient-to-br from-amber-500/[0.10] via-zinc-950 to-black'
        : 'border-white/10 bg-zinc-950'
    }`}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/45 font-bold">
      <Icon className={`w-3.5 h-3.5 ${gold ? 'text-gold-300' : 'text-white/55'}`} />
      {label}
    </div>
    <div className={`mt-2 text-3xl sm:text-4xl font-black tracking-tight ${gold ? 'text-gold-200' : 'text-white'}`}>
      {value}
    </div>
    {sub && <div className="mt-1 text-[12px] text-white/45">{sub}</div>}
  </div>
);

const StatusPill = ({ status }) => {
  const cfg = {
    approved: { cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', label: 'Approved' },
    paid: { cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', label: 'Paid' },
    pending: { cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30', label: 'Pending' },
    rejected: { cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30', label: 'Rejected' },
  };
  const m = cfg[status] || cfg.pending;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider ${m.cls}`}>
      {m.label}
    </span>
  );
};

const EarnCard = ({ icon: Icon, badge, title, reward, description, defaultOpen = false, testid, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className="rounded-3xl border border-white/10 bg-zinc-950 overflow-hidden transition-all hover:border-gold-500/30"
      data-testid={testid}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left p-5 sm:p-6 flex items-center gap-4"
      >
        <div className="w-12 h-12 rounded-2xl bg-gold-500/10 border border-gold-500/25 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-gold-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base sm:text-lg font-black text-white">{title}</h3>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gold-500/15 border border-gold-500/30 text-[10px] font-bold text-gold-300">
              {reward}
            </span>
            {badge && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[10px] font-bold text-emerald-300">
                {badge}
              </span>
            )}
          </div>
          <p className="mt-1 text-[13px] text-white/55 leading-relaxed">{description}</p>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-white/40" /> : <ChevronDown className="w-5 h-5 text-white/40" />}
      </button>

      {open && (
        <div className="px-5 sm:px-6 pb-6 border-t border-white/[0.06] pt-5">
          {children}
        </div>
      )}
    </div>
  );
};

const MissionCard = ({ icon: Icon, title, desc, current, target, reward, done }) => {
  const pct = Math.min(100, Math.round((current / target) * 100));
  return (
    <div
      className={`rounded-2xl border p-4 sm:p-5 ${
        done ? 'border-emerald-500/40 bg-emerald-500/[0.05]' : 'border-white/10 bg-zinc-950'
      }`}
      data-testid={`mission-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-xl border flex items-center justify-center flex-shrink-0 ${
          done ? 'bg-emerald-500/15 border-emerald-500/30' : 'bg-gold-500/10 border-gold-500/25'
        }`}>
          {done ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Icon className="w-4 h-4 text-gold-300" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h4 className="text-[14px] font-bold text-white">{title}</h4>
            <span className="text-[11px] font-bold text-gold-300">{reward}</span>
          </div>
          <p className="text-[12px] text-white/55 mt-0.5">{desc}</p>
          <div className="mt-3 flex items-center gap-2">
            <div className="flex-1 h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
              <div
                className={`h-full transition-all duration-700 ${done ? 'bg-emerald-400' : 'bg-gradient-to-r from-amber-400 to-gold-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-[11px] font-bold text-white/50 tabular-nums">
              {Math.min(current, target)}/{target}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ----- Main page --------------------------------------------------------- */

const InnerCenter = () => {
  const navigate = useNavigate();
  const token = typeof window !== 'undefined' ? localStorage.getItem('nofee_customer_token') : null;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate('/customer?login=1', { replace: true });
      return;
    }
    axios
      .get(`${API}/customers/me/rewards`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem('nofee_customer_token');
          navigate('/customer?login=1', { replace: true });
        } else {
          toast.error('Failed to load your rewards.');
        }
      })
      .finally(() => setLoading(false));
  }, [token, navigate]);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/customers/me/rewards`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch {
      /* noop */
    }
  };

  const fullReferralUrl = useMemo(() => {
    if (!data?.summary?.referral_code) return '';
    const code = data.summary.referral_code.toLowerCase();
    return typeof window !== 'undefined' ? `${window.location.origin}/r/${code}` : `/r/${code}`;
  }, [data]);

  const recordShare = async (platform) => {
    try {
      const res = await axios.post(
        `${API}/customers/share`,
        { network: platform, url: fullReferralUrl },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.awarded) {
        toast.success(`+${res.data.points} points!`);
        refresh();
      }
    } catch {
      /* silent — share UX must not be blocked by analytics */
    }
  };

  const logout = () => {
    localStorage.removeItem('nofee_customer_token');
    navigate('/customer?login=1', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gold-400 font-bold animate-pulse">Loading your rewards…</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white/60">Could not load rewards.</div>
      </div>
    );
  }

  const summary = data.summary || {};
  const cfg = data.config || {};
  const tier = data.tier || {};
  const history = data.history || [];
  const restRefs = data.restaurant_referrals || [];
  const friends = data.friends_invited || [];

  // Derive wallet numbers
  const giftCents = Number(summary.gift_credit_cents || 0);
  const convertibleCents = Number(summary.convertible_gift_credit_cents || 0);
  const totalPoints = Number(summary.total_points || 0);
  const pointsToNext = Number(summary.points_to_next_reward || 0);

  // Lifetime earned (sum of approved positive ledger amounts) — convert to dollars-equivalent (display)
  const pointsPerDollar = Number(cfg.points_per_dollar_gift_credit || 20);
  const lifetimeApprovedPts = history
    .filter((h) => (h.status === 'approved' || h.status === 'paid') && Number(h.amount) > 0)
    .reduce((s, h) => s + Number(h.amount || 0), 0);

  // Mission progress (derived from data we already have)
  const approvedRestaurantRefs = restRefs.filter((r) => (r.subscription_status || '').toLowerCase() === 'active').length;
  const friendsWithFirstOrder = friends.filter((f) => f.first_order_recorded).length;
  const shareCount = history.filter((h) => h.kind === 'social_share' && h.status === 'approved').length;

  const missions = [
    {
      icon: Users,
      title: 'First successful referral',
      desc: 'Get your first friend to place an order.',
      current: friendsWithFirstOrder,
      target: 1,
      reward: '+$1 credit',
      done: friendsWithFirstOrder >= 1,
    },
    {
      icon: Share2,
      title: 'Share 5 times',
      desc: 'Spread NoFee across 5 social posts or messages.',
      current: shareCount,
      target: 5,
      reward: '+$5 bonus',
      done: shareCount >= 5,
    },
    {
      icon: Store,
      title: 'Invite 3 restaurants',
      desc: 'Refer 3 restaurants that activate a NoFee plan.',
      current: approvedRestaurantRefs,
      target: 3,
      reward: '+$60 ($20 each)',
      done: approvedRestaurantRefs >= 3,
    },
    {
      icon: Sparkle,
      title: 'First order placed',
      desc: 'Place your first NoFee order to unlock loyalty bonus.',
      current: summary.first_order_recorded ? 1 : 0,
      target: 1,
      reward: 'Loyalty boost',
      done: !!summary.first_order_recorded,
    },
  ];

  // Combined referral history: restaurant + friend
  const referralHistory = [
    ...restRefs.map((r) => ({
      kind: 'Restaurant',
      name: r.name,
      status: (r.subscription_status || '').toLowerCase() === 'active' ? 'paid' : 'pending',
      reward: '$20',
      date: r.created_at,
    })),
    ...friends.map((f) => ({
      kind: 'Friend',
      name: f.name || f.email,
      status: f.first_order_recorded ? 'paid' : 'pending',
      reward: '$1',
      date: f.created_at,
    })),
  ].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur-md border-b border-white/[0.05]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-black tracking-tight">
            <span className="text-white">No</span>
            <span className="bg-gradient-to-b from-gold-400 to-gold-600 bg-clip-text text-transparent">Fee</span>
            <span className="ml-2 text-[10px] uppercase tracking-[0.22em] text-gold-400 font-bold">Rewards</span>
          </Link>
          <button
            onClick={logout}
            className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition-colors"
            data-testid="rewards-logout"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        {/* SECTION 1 — HERO */}
        <section className="mb-10 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-500/30 bg-gold-500/5 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-gold-300" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-gold-300 font-bold">
              {tier.tier_label || 'Member'}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            Earn Rewards
            <br />
            <span className="bg-gradient-to-r from-amber-200 via-gold-400 to-amber-300 bg-clip-text text-transparent">
              with NoFee.
            </span>
          </h1>
          <p className="mt-4 max-w-2xl text-base text-white/65 leading-relaxed">
            Invite friends, restaurants, and share NoFee to earn credits you can use at participating restaurants.
          </p>

          {/* Stat strip */}
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatCard
              testid="stat-available"
              gold
              icon={Wallet}
              label="Available"
              value={`$${(giftCents / 100).toFixed(2)}`}
              sub="Ready to use"
            />
            <StatCard
              testid="stat-pending"
              icon={Clock}
              label="Pending"
              value={totalPoints.toLocaleString()}
              sub={`${pointsPerDollar} pts = $1.00`}
            />
            <StatCard
              testid="stat-lifetime"
              icon={TrendingUp}
              label="Lifetime"
              value={lifetimeApprovedPts.toLocaleString()}
              sub="Points earned"
            />
            <StatCard
              testid="stat-referrals"
              icon={Users}
              label="Referrals"
              value={(restRefs.length + friends.length).toLocaleString()}
              sub={`${friends.length} friends · ${restRefs.length} restaurants`}
            />
          </div>
        </section>

        {/* SECTION 2 — NoFee Wallet */}
        <section className="mb-10 sm:mb-12" data-testid="wallet-section">
          <div className="rounded-3xl border border-gold-500/30 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-6 sm:p-8 relative overflow-hidden">
            <div className="pointer-events-none absolute -top-12 -right-12 w-64 h-64 rounded-full bg-gold-500/[0.08] blur-3xl" />

            <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold-500/30 bg-gold-500/5 mb-3">
                  <Wallet className="w-3 h-3 text-gold-300" />
                  <span className="text-[10px] uppercase tracking-[0.22em] text-gold-300 font-bold">
                    NoFee Wallet
                  </span>
                </div>
                <div className="text-5xl sm:text-6xl font-black tracking-tight text-white">
                  ${(giftCents / 100).toFixed(2)}
                </div>
                <p className="mt-1.5 text-sm text-white/55">
                  Available credit · Use during checkout at participating restaurants.
                </p>

                <div className="mt-5 grid grid-cols-3 gap-3 max-w-md">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 font-bold">Pending</div>
                    <div className="mt-1 text-base font-bold text-amber-300">
                      {totalPoints.toLocaleString()} pts
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 font-bold">Convertible</div>
                    <div className="mt-1 text-base font-bold text-white">
                      ${(convertibleCents / 100).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 font-bold">Earned</div>
                    <div className="mt-1 text-base font-bold text-white">
                      {lifetimeApprovedPts.toLocaleString()} pts
                    </div>
                  </div>
                </div>

                {pointsToNext > 0 && (
                  <div className="mt-5 max-w-md">
                    <div className="flex items-center justify-between text-[11px] mb-1.5">
                      <span className="text-white/55">Next $1 credit</span>
                      <span className="text-gold-300 font-bold">{pointsToNext} pts away</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-400 to-gold-500 transition-all duration-700"
                        style={{ width: `${100 - Math.min(100, (pointsToNext / pointsPerDollar) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <a
                  href="#wallet-activity"
                  data-testid="view-wallet-activity"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl border border-white/15 hover:border-gold-400/40 text-white font-bold text-sm transition-all"
                >
                  Wallet Activity
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3 — WAYS TO EARN */}
        <section className="mb-10 sm:mb-12" data-testid="ways-to-earn">
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight">Ways to earn</h2>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-bold">4 ways</span>
          </div>

          <div className="space-y-3">
            {/* CARD 1 — Restaurant referral */}
            <EarnCard
              testid="earn-card-restaurant"
              icon={Store}
              title="Invite a Restaurant"
              reward="+$20 in credits"
              description="When a restaurant signs up and activates a plan using your referral code, you earn $20."
              defaultOpen
            >
              <AmbassadorShareCard
                referralCode={summary.referral_code}
                shareUrl={fullReferralUrl}
                onShare={recordShare}
              />
            </EarnCard>

            {/* CARD 2 — Friend referral */}
            <EarnCard
              testid="earn-card-friend"
              icon={UserPlus}
              title="Invite a Friend"
              reward="+$1 per friend"
              description="When a friend creates an account using your link and places their first order, you earn a credit."
            >
              <AmbassadorShareCard
                referralCode={summary.referral_code}
                shareUrl={fullReferralUrl}
                onShare={recordShare}
              />
            </EarnCard>

            {/* CARD 3 — Social share */}
            <EarnCard
              testid="earn-card-social"
              icon={Megaphone}
              title="Share NoFee on Social Media"
              reward={`+${cfg.points_per_social_share || 20} pts per platform / day`}
              description="Post your referral link on Instagram, TikTok, X, Facebook, WhatsApp. Earn points each day."
            >
              <p className="text-[12px] text-white/50 mb-3">
                Limited to one award per platform per day — anti-fraud protected.
              </p>
              <AmbassadorShareCard
                referralCode={summary.referral_code}
                shareUrl={fullReferralUrl}
                onShare={recordShare}
              />
            </EarnCard>

            {/* CARD 4 — Bonus Missions */}
            <EarnCard
              testid="earn-card-missions"
              icon={Trophy}
              title="Bonus Missions"
              reward="Hit milestones"
              description="Complete missions to unlock extra credits and badges."
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {missions.map((m) => (
                  <MissionCard key={m.title} {...m} />
                ))}
              </div>
            </EarnCard>
          </div>
        </section>

        {/* SECTION 4 — REFERRAL HISTORY */}
        <section className="mb-10 sm:mb-12" data-testid="referral-history">
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight">Referral history</h2>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-bold">
              {referralHistory.length} total
            </span>
          </div>

          {referralHistory.length === 0 ? (
            <div className="rounded-3xl border border-white/10 bg-zinc-950 p-10 text-center">
              <Users className="w-8 h-8 text-white/20 mx-auto mb-3" />
              <p className="text-sm text-white/55">
                No referrals yet. Share your link to start earning.
              </p>
            </div>
          ) : (
            <div className="rounded-3xl border border-white/10 bg-zinc-950 overflow-hidden">
              <div className="grid grid-cols-[1.4fr_1fr_1fr_0.8fr] sm:grid-cols-[2fr_1fr_1fr_0.8fr] gap-0 border-b border-white/10 bg-white/[0.02]">
                {['Referral', 'Type', 'Status', 'Reward'].map((h) => (
                  <div key={h} className="px-4 sm:px-6 py-3 text-[10px] uppercase tracking-[0.18em] text-white/40 font-bold">
                    {h}
                  </div>
                ))}
              </div>
              {referralHistory.map((r, idx) => (
                <div
                  key={`${r.kind}-${idx}`}
                  className={`grid grid-cols-[1.4fr_1fr_1fr_0.8fr] sm:grid-cols-[2fr_1fr_1fr_0.8fr] gap-0 items-center ${
                    idx !== referralHistory.length - 1 ? 'border-b border-white/[0.04]' : ''
                  }`}
                >
                  <div className="px-4 sm:px-6 py-3 text-[14px] text-white truncate">{r.name || '—'}</div>
                  <div className="px-4 sm:px-6 py-3 text-[13px] text-white/60">{r.kind}</div>
                  <div className="px-4 sm:px-6 py-3">
                    <StatusPill status={r.status} />
                  </div>
                  <div className="px-4 sm:px-6 py-3 text-[13px] font-bold text-gold-300">{r.reward}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* SECTION 5 — WALLET ACTIVITY */}
        <section id="wallet-activity" className="mb-10 sm:mb-12 scroll-mt-24" data-testid="wallet-activity">
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight">Wallet activity</h2>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-bold">
              Last {history.length} entries
            </span>
          </div>

          {history.length === 0 ? (
            <div className="rounded-3xl border border-white/10 bg-zinc-950 p-10 text-center">
              <Coins className="w-8 h-8 text-white/20 mx-auto mb-3" />
              <p className="text-sm text-white/55">No activity yet. Start earning above.</p>
            </div>
          ) : (
            <div className="rounded-3xl border border-white/10 bg-zinc-950 overflow-hidden">
              <div className="grid grid-cols-[1.5fr_0.8fr_0.8fr_1fr] gap-0 border-b border-white/10 bg-white/[0.02]">
                {['Action', 'Points', 'Status', 'Date'].map((h) => (
                  <div key={h} className="px-4 sm:px-6 py-3 text-[10px] uppercase tracking-[0.18em] text-white/40 font-bold">
                    {h}
                  </div>
                ))}
              </div>
              {history.slice(0, 25).map((h, idx) => {
                const amt = Number(h.amount || 0);
                const isPos = amt > 0;
                const niceKind =
                  {
                    social_share: 'Social share reward',
                    order_paid: 'Order points earned',
                    referral_signup: 'Friend signed up',
                    referral_first_order: 'Friend first order',
                    restaurant_referral_approved: 'Restaurant referral approved',
                    redemption: 'Credit applied to wallet',
                  }[h.kind] || h.kind?.replaceAll('_', ' ');
                return (
                  <div
                    key={h.id || idx}
                    className={`grid grid-cols-[1.5fr_0.8fr_0.8fr_1fr] gap-0 items-center ${
                      idx !== history.length - 1 ? 'border-b border-white/[0.04]' : ''
                    }`}
                  >
                    <div className="px-4 sm:px-6 py-3 text-[14px] text-white truncate capitalize">{niceKind}</div>
                    <div className={`px-4 sm:px-6 py-3 text-[14px] font-bold tabular-nums ${isPos ? 'text-emerald-300' : 'text-rose-300'}`}>
                      {isPos ? '+' : ''}{amt}
                    </div>
                    <div className="px-4 sm:px-6 py-3">
                      <StatusPill status={h.status} />
                    </div>
                    <div className="px-4 sm:px-6 py-3 text-[12px] text-white/45">
                      {h.created_at ? new Date(h.created_at).toLocaleDateString() : '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* SECTION 6 — HOW TO USE CREDITS */}
        <section className="mb-10" data-testid="how-credits-work">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-5">How to use your credits</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Example UI */}
            <div className="rounded-3xl border border-gold-500/30 bg-gradient-to-br from-zinc-950 to-black p-6 sm:p-7">
              <p className="text-[11px] uppercase tracking-[0.22em] text-gold-300 font-bold mb-3">
                Checkout preview
              </p>
              <div className="rounded-2xl border border-white/10 bg-black/60 p-5">
                <div className="space-y-2.5 text-[14px]">
                  <div className="flex items-center justify-between">
                    <span className="text-white/70">Subtotal</span>
                    <span className="text-white font-semibold tabular-nums">$28.00</span>
                  </div>
                  <div className="flex items-center justify-between text-gold-300">
                    <span>NoFee Credits applied</span>
                    <span className="font-bold tabular-nums">−$5.00</span>
                  </div>
                  <div className="border-t border-white/10 pt-2.5 flex items-center justify-between">
                    <span className="text-white font-bold">Final total</span>
                    <span className="text-white font-black text-lg tabular-nums">$23.00</span>
                  </div>
                </div>
              </div>
              <p className="mt-4 text-[12px] text-white/45 leading-relaxed">
                Credits can be applied during checkout at any participating restaurant — no codes required.
              </p>
            </div>

            {/* Rules / instructions */}
            <div className="rounded-3xl border border-white/10 bg-zinc-950 p-6 sm:p-7">
              <p className="text-[11px] uppercase tracking-[0.22em] text-white/45 font-bold mb-3">
                How it works
              </p>
              <ul className="space-y-3">
                {[
                  ['Earn points', `Every $1 spent earns ${cfg.points_per_dollar_spent || 1} point. Referrals & shares earn more.`],
                  ['Convert to credits', `${pointsPerDollar} points = $1 in NoFee Credits. Convert anytime.`],
                  ['Spend at checkout', 'Use credits during checkout — they apply automatically to your total.'],
                  ['No expiration', 'Your credits never expire. Save them for a big order.'],
                ].map(([title, body]) => (
                  <li key={title} className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-gold-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[14px] font-bold text-white">{title}</div>
                      <div className="text-[13px] text-white/55">{body}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <p className="text-center text-[11px] text-white/30 mt-8">
          {cfg.disclaimer || 'Rewards subject to NoFee Rewards Program Terms.'}
        </p>
      </main>
    </div>
  );
};

const RewardsCenter = () => <InnerCenter />;

export default RewardsCenter;
